/**
 * Latency Trends Chart Component
 *
 * Area chart showing retrieval and LLM generation latency over time
 */

import { useId, useMemo, useCallback } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type { LegendPayload } from 'recharts/types/component/DefaultLegendContent';
import { formatChartTime, formatLatencyCompact } from '@/lib/formatters';
import { ChartCard, CHART_HEIGHT_CLASSES } from './ChartCard';
import {
  createDotProps,
  createActiveDotProps,
  createGradientIds,
} from './chart-utils';
import { MetricTooltip } from './MetricTooltip';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import type { DataPoint } from '@/types/dashboard';

interface LatencyTrendsChartProps {
  retrievalData: DataPoint[] | undefined;
  llmData: DataPoint[] | undefined;
  ttftData: DataPoint[] | undefined;
  isLoading: boolean;
  avgRetrievalLatency?: number;
  avgLlmLatency?: number;
  avgTtft?: number;
}

interface MergedLatencyPoint {
  time: string;
  timestamp: string; // Original ISO timestamp for sorting
  retrieval: number | null;
  llm: number | null;
  ttft: number | null;
}

function mergeLatencyData(
  retrievalData: DataPoint[] | undefined,
  llmData: DataPoint[] | undefined,
  ttftData: DataPoint[] | undefined,
): MergedLatencyPoint[] {
  const dataMap = new Map<string, MergedLatencyPoint>();

  // Add retrieval data
  // Use original timestamp as key to properly merge data points
  retrievalData?.forEach((point) => {
    dataMap.set(point.timestamp, {
      time: formatChartTime(point.timestamp),
      timestamp: point.timestamp,
      retrieval: Math.round(point.value),
      llm: null,
      ttft: null,
    });
  });

  // Merge LLM data
  llmData?.forEach((point) => {
    const existing = dataMap.get(point.timestamp);
    if (existing) {
      existing.llm = Math.round(point.value);
    } else {
      dataMap.set(point.timestamp, {
        time: formatChartTime(point.timestamp),
        timestamp: point.timestamp,
        retrieval: null,
        llm: Math.round(point.value),
        ttft: null,
      });
    }
  });

  // Merge TTFT data
  ttftData?.forEach((point) => {
    const existing = dataMap.get(point.timestamp);
    if (existing) {
      existing.ttft = Math.round(point.value);
    } else {
      dataMap.set(point.timestamp, {
        time: formatChartTime(point.timestamp),
        timestamp: point.timestamp,
        retrieval: null,
        llm: null,
        ttft: Math.round(point.value),
      });
    }
  });

  // Sort chronologically by original timestamp
  return Array.from(dataMap.values()).sort((a, b) =>
    a.timestamp.localeCompare(b.timestamp),
  );
}

const chartConfig = {
  retrieval: {
    label: 'Vector Search',
    color: '#2563eb', // blue-600
  },
  llm: {
    label: 'LLM Generation',
    color: '#60a5fa', // blue-400
  },
  ttft: {
    label: 'Time to First Token',
    color: '#22c55e', // green-500
  },
} satisfies ChartConfig;

export function LatencyTrendsChart({
  retrievalData,
  llmData,
  ttftData,
  isLoading,
  avgRetrievalLatency,
  avgLlmLatency,
  avgTtft,
}: LatencyTrendsChartProps) {
  // Generate unique gradient IDs for this chart instance
  const chartId = useId();
  const gradientIds = useMemo(
    () => createGradientIds(chartId, ['fillRetrieval', 'fillLlm', 'fillTtft']),
    [chartId],
  );

  // Memoize data transformation to avoid recalculating on every render
  const chartData = useMemo(
    () => mergeLatencyData(retrievalData, llmData, ttftData),
    [retrievalData, llmData, ttftData],
  );

  // Memoize legend reordering callback
  const renderLegend = useCallback(
    (props: { payload?: readonly LegendPayload[] }) => {
      const reorderedPayload = props.payload
        ? [...props.payload].sort((a, b) => {
            const order = ['retrieval', 'ttft', 'llm'];
            return (
              order.indexOf(String(a.dataKey)) -
              order.indexOf(String(b.dataKey))
            );
          })
        : [];
      return <ChartLegendContent {...props} payload={reorderedPayload} />;
    },
    [],
  );

  // Show skeleton when loading OR when data hasn't arrived yet (props are undefined)
  const showSkeleton =
    isLoading ||
    (retrievalData === undefined &&
      llmData === undefined &&
      ttftData === undefined);

  // Show "no data" only when data has loaded but is genuinely empty
  const isEmpty = !showSkeleton && chartData.length === 0;

  // Header summary content with tooltips for metric explanations
  const headerContent = (avgRetrievalLatency !== undefined ||
    avgLlmLatency !== undefined ||
    avgTtft !== undefined) && (
    <div className="flex gap-4 text-sm text-muted-foreground">
      {avgRetrievalLatency !== undefined && (
        <MetricTooltip
          label="Avg Retrieval"
          value={formatLatencyCompact(avgRetrievalLatency)}
          tooltip={METRIC_DESCRIPTIONS.avgRetrieval}
        />
      )}
      {avgTtft !== undefined && (
        <MetricTooltip
          label="Avg TTFT"
          value={formatLatencyCompact(avgTtft)}
          tooltip={METRIC_DESCRIPTIONS.avgTtft}
        />
      )}
      {avgLlmLatency !== undefined && (
        <MetricTooltip
          label="Avg LLM"
          value={formatLatencyCompact(avgLlmLatency)}
          tooltip={METRIC_DESCRIPTIONS.avgLlm}
        />
      )}
    </div>
  );

  return (
    <ChartCard
      title="Latency Trends"
      tooltip={METRIC_DESCRIPTIONS.latencyTrends}
      headerContent={headerContent}
      isLoading={showSkeleton}
      isEmpty={isEmpty}
      emptyAriaLabel="No latency data available"
    >
      <ChartContainer
        config={chartConfig}
        className={`${CHART_HEIGHT_CLASSES} w-full`}
      >
        <AreaChart data={chartData} accessibilityLayer>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="time"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            allowDecimals={false}
            unit=" ms"
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend content={renderLegend} />
          <defs>
            <linearGradient
              id={gradientIds.fillRetrieval}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor="var(--color-retrieval)"
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor="var(--color-retrieval)"
                stopOpacity={0.1}
              />
            </linearGradient>
            <linearGradient
              id={gradientIds.fillLlm}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor="var(--color-llm)"
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor="var(--color-llm)"
                stopOpacity={0.1}
              />
            </linearGradient>
            <linearGradient
              id={gradientIds.fillTtft}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor="var(--color-ttft)"
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor="var(--color-ttft)"
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <Area
            dataKey="llm"
            name="LLM Generation"
            type="monotone"
            fill={`url(#${gradientIds.fillLlm})`}
            fillOpacity={0.4}
            stroke="var(--color-llm)"
            strokeWidth={2}
            connectNulls
            dot={createDotProps(chartData.length, 'var(--color-llm)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-llm)',
            )}
          />
          <Area
            dataKey="ttft"
            name="Time to First Token"
            type="monotone"
            fill={`url(#${gradientIds.fillTtft})`}
            fillOpacity={0.4}
            stroke="var(--color-ttft)"
            strokeWidth={2}
            connectNulls
            dot={createDotProps(chartData.length, 'var(--color-ttft)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-ttft)',
            )}
          />
          <Area
            dataKey="retrieval"
            name="Vector Search"
            type="monotone"
            fill={`url(#${gradientIds.fillRetrieval})`}
            fillOpacity={0.4}
            stroke="var(--color-retrieval)"
            strokeWidth={2}
            connectNulls
            dot={createDotProps(chartData.length, 'var(--color-retrieval)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-retrieval)',
            )}
          />
        </AreaChart>
      </ChartContainer>
    </ChartCard>
  );
}
